import {
  useCallback, useEffect, useMemo,
} from 'react';
import type { LatLng } from '~/_shared/types/latLng';
import { ProximityType } from '~/_shared/types/proximity/proximity.enums';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { isArray } from '~/_shared/utils/collections/collections';
import {
  getAppleMapsNavigateToLatLngLink, getGoogleMapsNavigateToLatLngLink,
} from '~/_shared/utils/link/gisLink';
import { useNavigationDisclaimerConfirmation } from '~/_shared/utils/link/useNavigationDisclaimerConfirmation.hook';
import { createRightSidebarContextMenuCallbackProps } from '~/customizeLocationPanel/roundButtonMenu/contextMenuStructure/editLocationCallbacks.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import { useExportReport } from '~/exportReport/useExportReport';
import { useIsExportReportAvailable } from '~/exportReport/useIsExportReportAvailable';
import { useExportReportValidation } from '~/exportReport/validation/useExportReportValidation';
import { useProcessMarkersForRouting } from '~/map/map/lassoTool/optimizeRouteBetweenPoints/useProcessMarkersForRouting';
import { type RowsPerSpreadsheet } from '~/map/map/lassoTool/rowsPerSpreadsheet';
import { useDrawRadiusOnMarker } from '~/map/map/proximity/useDrawRadiusOnMarker';
import { useIsDriveTimePolygonToolEnabled } from '~/proximity/panel/useIsDriveTimePolygonToolEnabled';
import type { SidebarTitle } from '~/sidebar/sidebarApps/rightSidebar/sidebarTitle/sidebarTitle.types';
import { usePublicMapSettingsSelector } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';

export const useToolLocationCallbacks = (
  spreadsheetRowId?: SpreadsheetRowId,
  locationLatLng?: LatLng | null,
  locationDescription?: SidebarTitle
) => {
  const { processSelectedData } = useProcessMarkersForRouting();
  const { addMarkerToProximityTool } = useDrawRadiusOnMarker();
  const isMapPresentational = useIsMapPresentationalSelector();
  const { routingDirections: isRoutingAllowed, radiusProximity: isProximityAllowed } = usePublicMapSettingsSelector();
  const { openExportReport } = useExportReport();
  const { isExportReportAvailable } = useIsExportReportAvailable();
  const isDriveTimePolygonToolEnabled = useIsDriveTimePolygonToolEnabled();
  const { validateReportLocation, reportValidationResult } = useExportReportValidation();
  const { confirmNavigationDisclaimer } = useNavigationDisclaimerConfirmation();

  const navigateToLink = useCallback((getLink: (latLng: LatLng) => string) => {
    if (locationLatLng) {
      confirmNavigationDisclaimer(() => {
        window.location.href = getLink(locationLatLng);
      });
    }
  }, [confirmNavigationDisclaimer, locationLatLng]);

  const createNavigateProps = useCallback((getLink: (latLng: LatLng) => string) => (
    locationLatLng
      ? createRightSidebarContextMenuCallbackProps({ callback: () => navigateToLink(getLink) })
      : undefined
  ), [locationLatLng, navigateToLink]);

  const addToRouteCallback = useCallback(
    () => {
      if (!spreadsheetRowId?.spreadsheetId || !spreadsheetRowId?.rowId) {
        return;
      }
      const rowsPerSpreadsheets: RowsPerSpreadsheet[] = [
        {
          spreadsheetId: spreadsheetRowId.spreadsheetId,
          rows: [{
            id: spreadsheetRowId.rowId,
          }],
        },
      ];
      processSelectedData(rowsPerSpreadsheets);
    },
    [processSelectedData, spreadsheetRowId],
  );

  const drawRadiusCallback = useCallback(() => {
    if (!spreadsheetRowId?.spreadsheetId || !spreadsheetRowId?.rowId) {
      return;
    }
    addMarkerToProximityTool(spreadsheetRowId, ProximityType.DistanceRadius);

  }, [addMarkerToProximityTool, spreadsheetRowId]);

  const viewSummaryCallback = useCallback(() => {
    if (!spreadsheetRowId?.spreadsheetId || !spreadsheetRowId?.rowId) {
      return;
    }

    let description = '';
    if (locationDescription?.description && isArray(locationDescription.description ?? '')) {
      description = locationDescription.description.join(', ');
    }
    if (typeof locationDescription?.description === 'string') {
      description = locationDescription.description;
    }

    openExportReport({
      type: ExportReportType.Location,
      data: {
        markerId: spreadsheetRowId,
        summary: {
          heading: typeof locationDescription?.header === 'string' ? locationDescription.header : '',
          semiHeading: typeof locationDescription?.semiHeader === 'string' ? locationDescription.semiHeader : '',
          description,
        },
      },
    });
  }, [spreadsheetRowId, locationDescription, openExportReport]);

  const addDTPCallback = useCallback(() => {
    if (!isDriveTimePolygonToolEnabled || !spreadsheetRowId?.spreadsheetId || !spreadsheetRowId?.rowId) {
      return;
    }
    addMarkerToProximityTool(spreadsheetRowId, ProximityType.DriveTimePolygon);

  }, [addMarkerToProximityTool, spreadsheetRowId, isDriveTimePolygonToolEnabled]);

  useEffect(() => {
    if (isExportReportAvailable && spreadsheetRowId) {
      validateReportLocation(spreadsheetRowId);
    }
  }, [isExportReportAvailable, spreadsheetRowId, validateReportLocation]);

  return useMemo(() => ({
    addDTPCallback: isMapPresentational ? undefined : addDTPCallback,
    addToRouteCallback: (!isMapPresentational || isRoutingAllowed) ? addToRouteCallback : undefined,
    drawRadiusCallback: (!isMapPresentational || isProximityAllowed) ? drawRadiusCallback : undefined,
    navigate: createNavigateProps(getGoogleMapsNavigateToLatLngLink),
    navigateApple: createNavigateProps(getAppleMapsNavigateToLatLngLink),
    viewSummaryCallback: isExportReportAvailable ? viewSummaryCallback : undefined,
    viewSummaryDisabled: isExportReportAvailable ? reportValidationResult : undefined,
  }), [addDTPCallback, addToRouteCallback, createNavigateProps, drawRadiusCallback, isExportReportAvailable,
    isMapPresentational, isProximityAllowed, isRoutingAllowed, viewSummaryCallback, reportValidationResult]);
};
